/**
 * useHydra — React hook that manages the HydraClient lifecycle.
 * Starts on mount, stops on unmount. Feeds data into the Zustand marketStore.
 */

import { useEffect, useRef } from 'react';
import { HydraClient } from '../clients/HydraClient';
import { useMarketStore } from '../stores/marketStore';

/**
 * Hydra consumer WS lives on the API port (8891), not on :443 — the public site is the Next dashboard.
 * Local Vite is http://localhost, so `ws://hydra.newbx.com:8891/ws` is allowed (no mixed content).
 * HTTPS deployments need WSS (env or a terminator that proxies to :8891); see `.env.example`.
 * Bearer auth on the consumer socket is not available from the browser; see `HydraClient`.
 */
function defaultHydraWsUrl(): string {
  if (import.meta.env.DEV) {
    return 'ws://hydra.newbx.com:8891/ws';
  }
  return 'wss://hydra.newbx.com/ws';
}

const HYDRA_URL = (import.meta.env.VITE_HYDRA_URL as string | undefined)?.trim() || defaultHydraWsUrl();

// Staleness thresholds for the resnapshot triggers. Background tabs get
// throttled by the browser (Chrome heavily after ~5 min) and the WS can
// die silently — `connected: true` plus a flat `lastMessageMs` is the
// signature. Visibility-return resnaps when we've been quiet for 30 s+
// (catches "I came back to the tab"); the watchdog fires at 60 s+ for
// the rare case where the tab stayed visible but went silent anyway.
const VISIBILITY_RESNAP_STALE_MS = 30_000;
const WATCHDOG_STALE_MS = 60_000;
const WATCHDOG_INTERVAL_MS = 30_000;

// Click-to-refresh: when the feed has been silent ≥1 s OR broad
// activity has collapsed (mirrors useFeedFreshness so the yellow dot
// and the click behavior agree), any user click anywhere on the page
// fires one resnapshot. Throttled so rapid clicking can't spam hydra.
const CLICK_REFRESH_STALE_MS = 1_000;
const CLICK_REFRESH_THROTTLE_MS = 2_000;
const CLICK_REFRESH_ACTIVITY_WINDOW_MS = 30_000;
const CLICK_REFRESH_ACTIVITY_THRESHOLD = 0.10;
const CLICK_REFRESH_ACTIVITY_MIN_POP = 20;

export function useHydra() {
  const clientRef = useRef<HydraClient | null>(null);
  const connected = useMarketStore((s) => s.connected);
  const marketsCount = useMarketStore((s) => s.marketsCount);
  const orderbooksCount = useMarketStore((s) => s.orderbooksCount);

  useEffect(() => {
    const client = new HydraClient(HYDRA_URL, {
      onMarket: (market) => useMarketStore.getState().ingestMarket(market),
      onOrderbook: (ticker, book) => useMarketStore.getState().ingestOrderbook(ticker, book),
      onConsensus: (consensus) => useMarketStore.getState().ingestConsensus(consensus),
      onActiveSports: (sports) => useMarketStore.getState().ingestActiveSports(sports),
      onStatus: (isConnected) => useMarketStore.getState().setConnected(isConnected),
      onBackpressure: (level) => useMarketStore.getState().setBackpressureLevel(level),
    });

    clientRef.current = client;
    useMarketStore.getState().setClient(client);
    client.start();

    // Fast-path drain on tab close. `pagehide` is more reliable than
    // `beforeunload` (fires on mobile + iOS Safari, doesn't break bfcache)
    // and lets the SharedWorker drop our orderbook needs from the union
    // immediately instead of waiting on its 30 s heartbeat reaper. The
    // reaper is still the load-bearing safety net for crashes / OS kills /
    // background eviction where pagehide never fires.
    //
    // Bfcache caveat: when `event.persisted` is true, the page is being
    // snapshotted for back/forward cache, NOT torn down. Calling stop()
    // here used to permanently orphan the tab — pagehide killed the
    // worker port, bfcache restore doesn't re-run React effects so
    // nothing restarted it, and the dashboard stayed connected-looking
    // but frozen forever. Skip stop() in that case and rely on the
    // worker's reaper-resurrect path + tab-side stall detector to
    // bring the connection back when the page resumes.
    const handlePageHide = (event: PageTransitionEvent) => {
      if (event.persisted) return;
      client.stop();
    };
    window.addEventListener('pagehide', handlePageHide);

    // Belt-and-suspenders for bfcache restore: even though we skip
    // stop() on persisted pagehide, the worker may have reaped our
    // port during freeze (heartbeat setInterval pauses in bfcache).
    // The worker's resurrection path handles that on its own, but if
    // the worker also died (server-side restart) or our SharedWorker
    // reference is otherwise sick, force a clean restart on resume.
    const handlePageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      client.restartShared('bfcache restore');
    };
    window.addEventListener('pageshow', handlePageShow);

    // Heal stale-on-return: when the tab becomes visible after time in the
    // background, ask upstream for a fresh markets snapshot if we haven't
    // heard anything in VISIBILITY_RESNAP_STALE_MS+. Cheap when fresh —
    // the threshold gates out rapid alt-tabs.
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      const lastMs = client.lastMessageMs;
      if (lastMs === 0) return;
      if (Date.now() - lastMs > VISIBILITY_RESNAP_STALE_MS) {
        client.requestResnapshot();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Watchdog for silent stalls while the tab stays visible (rare —
    // usually only after the WS layer thinks it's connected but upstream
    // dropped us without a clean close). Skip while hidden to avoid
    // waking a deliberately-throttled background tab.
    const watchdog = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      const lastMs = client.lastMessageMs;
      if (lastMs === 0) return;
      if (Date.now() - lastMs > WATCHDOG_STALE_MS) {
        client.requestResnapshot();
      }
    }, WATCHDOG_INTERVAL_MS);

    // Global click-to-refresh: any user click anywhere on the page
    // fires a full WS reconnect when the feed looks stale. Captures
    // clicks in the document's capture phase so every interaction
    // (buttons, chips, anything else interactive) triggers without
    // each component having to opt in. Throttled so a flurry of clicks
    // can't spam hydra.
    //
    // Why reconnect (not just resnapshot): the silent-stall failure
    // mode is "WS readyState OPEN, but no frames flow" — sending a
    // resnapshot request over that socket is itself a lost message.
    // A fresh WS rebuilds the connection and re-subscribes; that's
    // the only thing that actually recovers data flow.
    //
    // Reads from marketStore (not client.lastMessageMs) so we use the
    // same source of truth the yellow-dot indicator uses. HydraClient's
    // own lastMessageMs gets bumped by every SharedWorker port message
    // including __status / control frames, so it stays fresh even when
    // no real market data flows — the wrong signal here.
    let lastClickRefreshAt = 0;
    const handleClickRefresh = () => {
      const store = useMarketStore.getState();
      const lastMs = store.lastMessageMs;
      if (lastMs === 0) return; // initial load — let the connect snapshot carry it

      const ageMs = Date.now() - lastMs;
      const recentSilence = ageMs > CLICK_REFRESH_STALE_MS;
      const { active, total } = store.getActivityCount(CLICK_REFRESH_ACTIVITY_WINDOW_MS);
      const activityCollapse =
        total >= CLICK_REFRESH_ACTIVITY_MIN_POP &&
        active / total < CLICK_REFRESH_ACTIVITY_THRESHOLD;
      if (!recentSilence && !activityCollapse) return;

      const now = Date.now();
      if (now - lastClickRefreshAt < CLICK_REFRESH_THROTTLE_MS) return;
      lastClickRefreshAt = now;
      client.forceReconnect();
    };
    document.addEventListener('pointerdown', handleClickRefresh, { capture: true });

    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('pointerdown', handleClickRefresh, { capture: true });
      clearInterval(watchdog);
      useMarketStore.getState().setClient(null);
      client.stop();
      clientRef.current = null;
    };
  }, []);

  return { connected, marketsCount, orderbooksCount };
}
